import { DataTable } from 'mantine-datatable';
import { useLayoutEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

import { ActionIcon, Button, Group, Space, Text, Tooltip } from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import {
  IconChevronDown,
  IconChevronUp,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { priceToDecimal } from '@utils/price';

import {
  ShippingCost,
  useDecrementShippingCostPriorityMutation,
  useDeleteShippingCostMutation,
  useGetShippingCostsQuery,
  useIncrementShippingCostPriorityMutation,
} from '@api/shippingCosts.api';

import { LayoutContextType } from '@components/layout/Layout';
import UpsertShippingCostForm from '@components/UpsertShippingCostForm';

export default function ShippingCosts() {
  // ==========================================================================
  // General
  // ==========================================================================

  // ==========================================================================
  // State
  // ==========================================================================

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data = [], isLoading } = useGetShippingCostsQuery();

  const [deleteShippingCost, { isLoading: isLoadingDelete }] =
    useDeleteShippingCostMutation();

  const [incrementShippingCostPriority, { isLoading: isLoadingIncrement }] =
    useIncrementShippingCostPriorityMutation();
  const [decrementShippingCostPriority, { isLoading: isLoadingDecrement }] =
    useDecrementShippingCostPriorityMutation();

  // ==========================================================================
  // Form
  // ==========================================================================

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleCreateShippingCostClick = () => {
    openModal({
      title: 'Crea costo di spedizione',
      children: <UpsertShippingCostForm />,
    });
  };

  const handleUpdateShippingCostClick = (shippingCost: ShippingCost) => {
    openModal({
      title: 'Modifica costo di spedizione',
      children: <UpsertShippingCostForm shippingCost={shippingCost} />,
    });
  };

  const handleDeleteShippingCostClick = (id: number) => {
    openConfirmModal({
      title: 'Elimina costo di spedizione',
      children:
        'Eliminando il costo di spedizione questo non sarà più disponibile al checkout. Proseguire?',
      labels: {
        cancel: 'Annulla',
        confirm: 'Elimina costo di spedizione',
      },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteShippingCost(id),
    });
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    setLayoutProps({
      title: 'Costi di spedizione',
    });
  }, [setLayoutProps]);

  return (
    <>
      <Text maw={800}>
        I costi di spedizione sotto indicati verranno applicati in fase di
        checkout. Il costo valido (che rispetta le condizioni di valore minimo
        ordine e province) con priorità più bassa verrà applicato.
      </Text>
      <Button
        my="lg"
        onClick={handleCreateShippingCostClick}
        leftSection={<IconPlus />}
      >
        Aggiungi costo di spedizione
      </Button>
      <DataTable
        striped
        withTableBorder
        fetching={isLoading}
        noRecordsText="Nessun costo di spedizione trovato"
        minHeight={data.length === 0 ? 200 : undefined}
        columns={[
          {
            accessor: 'priority',
            title: 'Priorità',
            render: (record: ShippingCost, index: number) => (
              <Group gap={0}>
                {record.priority}
                {data.length > 1 && (
                  <>
                    <Space w="lg" />
                    {index !== 0 && (
                      <Tooltip label="Sposta sopra">
                        <ActionIcon
                          variant="subtle"
                          onClick={() =>
                            incrementShippingCostPriority(record.id)
                          }
                          loading={isLoadingIncrement || isLoadingDecrement}
                        >
                          <IconChevronUp />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {index !== data.length - 1 && (
                      <Tooltip label="Sposta sotto">
                        <ActionIcon
                          variant="subtle"
                          onClick={() =>
                            decrementShippingCostPriority(record.id)
                          }
                          loading={isLoadingIncrement || isLoadingDecrement}
                        >
                          <IconChevronDown />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </>
                )}
              </Group>
            ),
          },
          {
            accessor: 'value',
            title: 'Costo',
            render: (record) => `€ ${priceToDecimal(record.value)}`,
          },
          {
            accessor: 'minOrderValue',
            title: 'Valore min. ordine',
            render: (record) =>
              record.minOrderValue
                ? `€ ${priceToDecimal(record.minOrderValue)}`
                : '-',
          },
          {
            accessor: 'provinces',
            title: 'Province',
            render: (record) =>
              record.provinces && record.provinces.length > 0
                ? record.provinces.map((p) => p.provinceCode).join(', ')
                : 'Tutte le province',
          },
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right',
            width: '0%',
            render: (record) => (
              <Group wrap="nowrap">
                <Tooltip label="Modifica">
                  <ActionIcon
                    onClick={() => handleUpdateShippingCostClick(record)}
                  >
                    <IconPencil />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Elimina">
                  <ActionIcon
                    color="red"
                    onClick={() => handleDeleteShippingCostClick(record.id)}
                    loading={isLoadingDelete}
                  >
                    <IconTrash />
                  </ActionIcon>
                </Tooltip>
              </Group>
            ),
          },
        ]}
        records={data}
      />
    </>
  );
}
